/**
 * Hashing utilities for TaskFortress
 *
 * This module provides hashing functions for creating consistent,
 * deterministic hashes from input strings like email addresses.
 */

/**
 * Hash a string using SHA-256
 * @param input The string to hash
 * @returns A hex string representation of the hash
 */
export async function hashString(input: string): Promise<string> {
  // Encode the string as UTF-8
  const encoder = new TextEncoder()
  const data = encoder.encode(input)

  // Hash the data
  const hashBuffer = await crypto.subtle.digest("SHA-256", data)

  // Convert buffer to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")

  return hashHex
}

/**
 * Hash an email address for use as a storage key
 * @param email The email address to hash
 * @returns A hex string hash of the email
 */
export async function hashEmail(email: string): Promise<string> {
  // Normalize email to lowercase for consistent hashing
  const normalizedEmail = email.toLowerCase().trim()
  return await hashString(normalizedEmail)
}
